import React from 'react';
import { Link, Stack, Switch, Typography } from '@mui/material';
import { DisplayInformation, DisplayInformationRow } from 'components';
import { Tenant, useEditTenantMutation } from 'gql';
import { OrganizationDialog } from '../OrganizationDialog';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

interface OwnProps {
  tenant: Tenant;
}

export const OrganizationDetails: React.FC<OwnProps> = (props) => {
  const { formatMessage } = useIntl();
  const { tenant } = props;
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const isDialogVisible = searchParams.get('edit') === 'true';

  const editTenantMutation = useEditTenantMutation({
    onSuccess: () => {
      queryClient.invalidateQueries(['getTenantById']);
      queryClient.invalidateQueries(['findTenants']);
    }
  });

  const handleOpenDialog = () => {
    searchParams.set('edit', 'true');
    setSearchParams(searchParams);
  };

  const handleCloseDialog = () => {
    searchParams.delete('edit');
    setSearchParams(searchParams);
  };

  const handleToggleGlobalReport = (checked: boolean) => {
    editTenantMutation.mutate({
      input: {
        id: tenant.id ?? '',
        name: tenant.name ?? '',
        region: tenant.region ?? '',
        town: tenant.town ?? '',
        telephone: tenant.telephone ?? '',
        email: tenant.email,
        extensionTelephone: tenant.extensionTelephone,
        isIncludedInGlobalReport: checked,
      }
    });
  };

  return (
    <>
      <DisplayInformation title={formatMessage({ id: 'Information' })} onEdit={handleOpenDialog}>
        <DisplayInformationRow label={formatMessage({ id: 'Name' })}>
          <Typography variant='body2'>
            {tenant.name}
          </Typography>
        </DisplayInformationRow>
        <DisplayInformationRow label={formatMessage({ id: 'Identifier' })}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography variant='body2'>
              stats.rocqtr.org/<b>{tenant.identifier}</b>
            </Typography>
          </Stack>
        </DisplayInformationRow>
        <DisplayInformationRow label={formatMessage({ id: 'Region' })}>
          <Typography variant='body2'>
            {tenant.region}
          </Typography>
        </DisplayInformationRow>
        <DisplayInformationRow label={formatMessage({ id: 'City' })}>
          <Typography variant='body2'>
            {tenant.town}
          </Typography>
        </DisplayInformationRow>
        <DisplayInformationRow label={formatMessage({ id: 'Telephone' })}>
          <Link color='info.main' variant='body2' href={`tel:${tenant.telephone}`}>
            {tenant.telephone}
          </Link>
          <Typography variant='body2'>{tenant.extensionTelephone ? `, ext. ${tenant.extensionTelephone}` : ''}</Typography>
        </DisplayInformationRow>
        <DisplayInformationRow label={formatMessage({ id: 'Email' })}>
          <Link color='info.main' variant='body2' href={`mailto:${tenant.email}`}>
            {tenant.email}
          </Link>
        </DisplayInformationRow>
        <OrganizationDialog
          organization={tenant}
          open={isDialogVisible}
          handleClose={handleCloseDialog}
        />
      </DisplayInformation>
      <DisplayInformation title={formatMessage({ id: 'Global report' })} sx={{ mt: 3 }}>
        <DisplayInformationRow>
          <Stack flex='1 1 200px'>
            <Typography variant='subtitle1' fontWeight={700}>{formatMessage({ id: 'Include in global report' })}</Typography>
            <Typography variant='body2'>{formatMessage({ id: 'When enabled, this organization\'s data will be included in global reports.' })}</Typography>
          </Stack>
          <Switch
            checked={tenant.isIncludedInGlobalReport ?? true}
            onChange={(e) => handleToggleGlobalReport(e.target.checked)}
            disabled={editTenantMutation.isLoading}
          />
        </DisplayInformationRow>
      </DisplayInformation>
    </>
  );
};